import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/solid';

const RentTrackerLandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Automated Rent Tracking',
      description: 'Track all rent payments in one centralized dashboard',
      icon: '📊',
    },
    {
      title: 'Payment Reminders',
      description: 'Send automated reminders to tenants before due dates',
      icon: '🔔',
    },
    {
      title: 'Tenant Communication',
      description: 'Manage all tenant messages in one easy-to-use interface',
      icon: '💬',
    },
    {
      title: 'Payment History',
      description: 'Complete payment history and financial reporting',
      icon: '📈',
    },
    {
      title: 'Late Payment Alerts',
      description: 'Instant notifications for overdue payments',
      icon: '⚠️',
    },
    {
      title: 'Multi-Property Support',
      description: 'Manage rent across all your properties at once',
      icon: '🏠',
    },
  ];

  const benefits = [
    'Save 5+ hours per month on rent management',
    'Reduce late payments by 30%',
    'Get detailed financial insights',
    'Automate routine tenant communication',
    'Access reports anytime, anywhere',
  ];

  const faqItems = [
    {
      question: 'Can I manage multiple tenants and properties?',
      answer:
        'Absolutely. Add unlimited properties, set individual due dates, and monitor payments per unit or across your portfolio.',
    },
    {
      question: 'Do tenants get automated reminders?',
      answer:
        'You can enable automated email reminders before and after due dates, plus instant late-payment alerts.',
    },
    {
      question: 'Can I export payment history for accounting?',
      answer:
        'Yes. Export CSV or PDF summaries for accountants, tax returns, or financial reporting in seconds.',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>
          Rent Tracker for Irish Landlords | Free Rent Payment Management | RentSmart.ie
        </title>
        <meta
          name="description"
          content="Simplify rent collection with RentSmart's free Rent Tracker. Track payments, send reminders, and manage tenants all in one place. Perfect for Irish landlords."
        />
        <meta
          name="keywords"
          content="rent tracker, rent payment tracking, landlord tools, rental management, Ireland, tenant management, rent collection, payment reminders"
        />
        <link rel="canonical" href="https://rentsmart.ie/property-tools/rent-tracker-landing" />
        <meta property="og:title" content="Rent Tracker - RentSmart.ie" />
        <meta
          property="og:description"
          content="Track rent payments, send reminders, and manage tenants with RentSmart's free Rent Tracker."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://rentsmart.ie/property-tools/rent-tracker-landing"
        />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Rent Tracking Made
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Effortless
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Track rent payments, send automated reminders, and manage your tenants all in one
              place. Free for Irish landlords.
            </p>
            <button
              onClick={() => navigate('/landlord-toolkit')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Get Started with Rent Tracker
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Powerful Features for Modern Landlords
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Choose RentSmart's Rent Tracker?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: 1,
                  title: 'Add Your Properties',
                  description: 'Register all your rental properties in minutes',
                },
                {
                  step: 2,
                  title: 'Set Up Tenants',
                  description: 'Add tenant details and payment schedules',
                },
                {
                  step: 3,
                  title: 'Track & Manage',
                  description: 'Track payments and manage communications automatically',
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Simplify Rent Management?
            </h2>
            <p className="text-lg mb-8 text-blue-100">
              Join hundreds of Irish landlords using RentSmart's Rent Tracker
            </p>
            <button
              onClick={() => navigate('/landlord-toolkit')}
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Access Rent Tracker Now
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">
              Rent Tracker FAQs for Irish Landlords
            </h2>
            <div className="space-y-6">
              {faqItems.map((item, index) => (
                <div
                  key={item.question}
                  className="bg-slate-50 border border-slate-200 rounded-lg p-6 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircleIcon className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{item.question}</h3>
                      <p className="text-gray-700 mt-2">{item.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default RentTrackerLandingPage;
